/**
 * @documental
 * @export Tag
 * @import
 * import { Tag } from "capitalrx-components-2.0/lib/general";
 */
import { FC, ReactNode } from "react";

import { createComponentStyleHook } from "capitalrx-components-2.0/lib/tokens";
import { Body, Semibold } from "capitalrx-components-2.0/lib/typography";
import classNames from "classnames";

import { BaseProps } from "../../BaseProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { Tooltip } from "../Tooltip";
import { TagType } from "./TagType";

import legacyStyles from "./legacy/styles.module.scss";
import tokenStyles from "./styles.module.css";

/**
 * The `Tag` component properties.
 */
export interface TagProps extends BaseProps {
  /**
   * Indicates the type of the tag. `"default"`, or no type, renders the light neutral style.
   * Every other type, including `"info"`, renders with a solid background.
   */
  readonly type?: TagType;

  /**
   * The tag tooltip text.
   */
  readonly tooltip?: ReactNode;

  /**
   * Indicates whether tag colors are inverted.
   * @deprecated Not part of the design system. Retained for existing call sites.
   */
  readonly inverted?: boolean;

  /** The component children. */
  readonly children?: ReactNode;

  /** The component role. */
  readonly role?: string;
}

const useResolvedTagStyles = createComponentStyleHook<TagProps>({
  legacyStyles,
  tokenStyles,
  componentTokens: "tag",
});

/**
 * Displays the child content within a tag of a specified type.
 */
export const Tag: FC<TagProps> = (props) => {
  const { type, inverted = false, tooltip, children, role = "status" } = props;
  const { mode, styles, StyleModeBoundary } = useResolvedTagStyles(props);

  /* A typeless tag uses the "default" style class so `type="info"` can render high contrast. */
  const styleType = type ?? "default";

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [classNames(styles.Tag, "tag", styleType, { inverted })],
    testIdPrefix: "tag",
  });

  return (
    <StyleModeBoundary mode={mode}>
      <Tooltip content={tooltip}>
        {(ref) => (
          <span ref={ref} {...baseHtmlProps} role={role}>
            <Body size="mini" inline>
              <Semibold>{children}</Semibold>
            </Body>
          </span>
        )}
      </Tooltip>
    </StyleModeBoundary>
  );
};
